<template>
  <v-tooltip bottom max-width="400" color="black">
    <template v-slot:activator="{ on, attrs }">
      <v-btn icon v-bind="attrs" v-on="on" @click="$emit('click')">
        <v-icon :color="color">{{ icon }}</v-icon>
      </v-btn>
    </template>

    <div class="py-2">
      <!-- Estado principal -->
      <div class="mb-2">
        <strong>{{ statusMessage }}</strong>
      </div>

      <!-- Métricas principales -->
      <div class="mb-2">
        <div>📊 <strong>Promedio:</strong> {{ avgText }}</div>
        <div>📉 <strong>Pérdida:</strong> {{ lossText }}</div>
        <div>📈 <strong>Muestras:</strong> {{ times.length }}/{{ windowSize }}</div>
      </div>

      <!-- Información adicional -->
      <div class="mb-2" style="border-top: 1px solid rgba(255,255,255,0.2); padding-top: 8px;">
        <div>⚡ <strong>Último ping:</strong> {{ lastPingText }}</div>
        <div>🎯 <strong>Mejor:</strong> {{ minTimeText }}</div>
        <div>⏱️ <strong>Peor:</strong> {{ maxTimeText }}</div>
        <div v-if="jitter > 0">📶 <strong>Jitter:</strong> {{ jitterText }}</div>
      </div>

      <!-- Estado de la conexión -->
      <div style="border-top: 1px solid rgba(255,255,255,0.2); padding-top: 8px;">
        <div>🌐 <strong>Estado:</strong> {{ connectionStatus }}</div>
        <div>🔄 <strong>Próximo chequeo:</strong> {{ nextCheckText }}</div>
        <div v-if="consecutiveFails > 0" class="error--text">
          ⚠️ <strong>Fallos consecutivos:</strong> {{ consecutiveFails }}
        </div>
      </div>

      <!-- Servidor actual -->
      <div v-if="urls.length > 1" style="border-top: 1px solid rgba(255,255,255,0.2); padding-top: 8px; font-size: 0.85em;">
        🖥️ Servidor: {{ currentServerName }}
      </div>
    </div>
  </v-tooltip>
</template>

<script>
import axios from "axios";

export default {
  name: "NetHealthIndicator",
  props: {
    icon: { type: String, default: "mdi-earth" },
    urls: { type: Array, default: () => ["https://mi.ubga.org.mx/healthcheck"] },
    localStorageName: { type: String, default: "ping-times" },
    goodMs: { type: Number, default: 200 },
    warnMs: { type: Number, default: 500 },
    windowSize: { type: Number, default: 20 },
    minIntervalOk: { type: Number, default: 30000 },
    maxIntervalOk: { type: Number, default: 60000 },
    retryInterval: { type: Number, default: 8000 },
    requestTimeoutMs: { type: Number, default: 5000 }
  },

  data: () => ({
    pos: 0,
    timerId: null,
    updateTimerId: null,
    times: [],
    avg: 0,
    loss: 0,
    color: "#9E9E9E",
    isOnline: navigator.onLine,
    statusMessage: "Analizando conexión...",
    lastPingTime: null,
    consecutiveFails: 0,
    nextCheckTime: null,
    lastSuccessfulPing: null,
    currentTime: Date.now()
  }),

  computed: {
    avgText() {
      if (!this.times.length) return "—";
      return `${Math.round(this.avg)} ms`;
    },

    lossText() {
      if (!this.times.length) return "—";
      return `${Math.round(this.loss * 100)}%`;
    },

    lastPingText() {
      if (this.lastPingTime === null) return "—";
      if (this.lastPingTime === -1) return "❌ Falló";
      return `${this.lastPingTime} ms`;
    },

    minTimeText() {
      const valid = this.times.filter(x => x !== -1);
      if (!valid.length) return "—";
      return `${Math.min(...valid)} ms`;
    },

    maxTimeText() {
      const valid = this.times.filter(x => x !== -1);
      if (!valid.length) return "—";
      return `${Math.max(...valid)} ms`;
    },

    jitter() {
      const valid = this.times.filter(x => x !== -1);
      if (valid.length < 2) return 0;
      
      let sum = 0;
      for (let i = 0; i < valid.length - 1; i++) {
        sum += Math.abs(valid[i] - valid[i + 1]);
      }
      return sum / (valid.length - 1);
    },

    jitterText() {
      return `${Math.round(this.jitter)} ms`;
    },

    connectionStatus() {
      if (!this.isOnline) return "❌ Sin Internet";
      if (this.loss >= 0.5) return "⚠️ Inestable";
      if (this.consecutiveFails > 0) return "⚠️ Intermitente";
      if (this.avg <= this.goodMs) return "✅ Excelente";
      if (this.avg <= this.warnMs) return "⚠️ Aceptable";
      return "❌ Deficiente";
    },

    nextCheckText() {
      if (!this.nextCheckTime) return "—";
      const seconds = Math.max(0, Math.ceil((this.nextCheckTime - this.currentTime) / 1000));
      if (seconds > 60) return `${Math.ceil(seconds / 60)} min`;
      return `${seconds}s`;
    },

    currentServerName() {
      const currentUrl = this.urls[this.pos === 0 ? this.urls.length - 1 : this.pos - 1];
      try {
        const url = new URL(currentUrl);
        return url.hostname;
      } catch {
        return currentUrl.substring(0, 30) + "...";
      }
    }
  },

  mounted() {
    try {
      const raw = localStorage.getItem(this.localStorageName);
      if (raw) {
        const data = JSON.parse(raw);
        this.times = data.times?.slice(0, this.windowSize) || data.slice(0, this.windowSize);
        this.lastSuccessfulPing = data.lastSuccessfulPing || null;
      }
    } catch (error) {
      console.log("Error loading ping data:", error);
    }

    this.pos = Math.floor(Math.random() * this.urls.length);
    this.updateStats();

    window.addEventListener("online", this.onOnline);
    window.addEventListener("offline", this.onOffline);

    // Actualizar el tiempo actual cada segundo para el contador
    this.updateTimerId = setInterval(() => {
      this.currentTime = Date.now();
    }, 1000);

    this.ping();
  },

  beforeDestroy() {
    if (this.timerId) clearTimeout(this.timerId);
    if (this.updateTimerId) clearInterval(this.updateTimerId);
    window.removeEventListener("online", this.onOnline);
    window.removeEventListener("offline", this.onOffline);
  },

  methods: {
    onOnline() {
      this.isOnline = true;
      this.statusMessage = "Reconectando...";
      this.scheduleNext(this.minIntervalOk);
    },

    onOffline() {
      this.isOnline = false;
      this.color = "#F44336";
      this.statusMessage = "Sin conexión a Internet";
      this.scheduleNext(this.retryInterval);
    },

    withJitter(ms) {
      const jitter = 0.2;
      const delta = (Math.random() * 2 - 1) * jitter * ms;
      return Math.max(1000, Math.round(ms + delta));
    },

    scheduleNext(baseMs) {
      if (this.timerId) clearTimeout(this.timerId);
      const actualMs = this.withJitter(baseMs);
      this.nextCheckTime = Date.now() + actualMs;
      this.timerId = setTimeout(this.ping, actualMs);
    },

    async ping() {
      this.timerId = null;
      const url = this.urls[this.pos];
      this.pos = (this.pos + 1) % this.urls.length;

      if (!navigator.onLine) {
        this.lastPingTime = -1;
        this.consecutiveFails++;
        this.pushTime(-1);
        this.updateStats();
        return this.scheduleNext(this.retryInterval);
      }

      const started = performance.now();
      try {
        await axios({
          method: "head",
          url,
          timeout: this.requestTimeoutMs,
          headers: { "Cache-Control": "no-cache" },
          params: { _t: Date.now() }
        });
        const ms = Math.round(performance.now() - started);
        this.lastPingTime = ms;
        this.consecutiveFails = 0;
        this.lastSuccessfulPing = Date.now();
        this.pushTime(ms);
      } catch (error) {
        this.lastPingTime = -1;
        this.consecutiveFails++;
        this.pushTime(-1);
      } finally {
        this.updateStats();

        if (this.loss >= 0.5 || !this.isOnline) {
          this.scheduleNext(this.retryInterval);
        } else if (this.avg <= this.goodMs) {
          this.scheduleNext(this.maxIntervalOk);
        } else {
          this.scheduleNext(this.minIntervalOk);
        }
      }
    },

    pushTime(ms) {
      this.times.unshift(ms);
      if (this.times.length > this.windowSize) this.times.length = this.windowSize;
      
      try {
        localStorage.setItem(this.localStorageName, JSON.stringify({
          times: this.times,
          lastSuccessfulPing: this.lastSuccessfulPing
        }));
      } catch (error) {
        console.log("Error saving ping data:", error);
      }
    },

    updateStats() {
      if (!this.times.length) {
        this.avg = 0;
        this.loss = 0;
        this.color = "#9E9E9E";
        this.statusMessage = "Analizando conexión...";
        return;
      }

      const fails = this.times.filter(x => x === -1).length;
      this.loss = fails / this.times.length;
      const effective = this.times.map(x => (x === -1 ? this.requestTimeoutMs : x));
      this.avg = effective.reduce((a, b) => a + b, 0) / effective.length;

      // Evaluar color + mensaje
      if (!this.isOnline) {
        this.color = "#F44336";
        this.statusMessage = "Sin conexión a Internet";
      } else if (this.loss >= 0.5) {
        this.color = "#F44336";
        this.statusMessage = `Conexión inestable (${Math.round(this.loss * 100)}% pérdida)`;
      } else if (this.consecutiveFails >= 3) {
        this.color = "#FF5722";
        this.statusMessage = `Fallos consecutivos (${this.consecutiveFails})`;
      } else if (this.avg <= this.goodMs) {
        this.color = "#4CAF50";
        this.statusMessage = "Conexión excelente";
      } else if (this.avg <= this.warnMs) {
        this.color = "#FFB300";
        this.statusMessage = `Conexión aceptable (${Math.round(this.avg)} ms)`;
      } else {
        this.color = "#F44336";
        this.statusMessage = `Alta latencia (${Math.round(this.avg)} ms)`;
      }
    }
  }
};
</script>

<style scoped>
.error--text {
  color: #ff5252;
}
</style>